import "../../design/navbar.css";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../../assets/logo.avif";

export default function Navbar() {
    const navigate = useNavigate();
    const location = useLocation();

    const [menuOpen, setMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    const navRef = useRef(null);
    const lastScrollY = useRef(0);

    // 1. სქროლის ქცევა (დამალვა/გამოჩენა + ბლური)
    useEffect(() => {
        const handleScroll = () => {
            const current = window.scrollY;
            if (current > lastScrollY.current && current > 80 && !menuOpen) {
                navRef.current.style.transform = "translateY(-120%)";
            } else {
                navRef.current.style.transform = "translateY(0)";
            }
            setIsScrolled(current > 40);
            lastScrollY.current = current;
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [menuOpen]);

    // 2. Body-ს სქროლის ჩაკეტვა მობილური მენიუს დროს
    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "auto";
    }, [menuOpen]);

    // 3. გარე კლიკზე მენიუს დახურვა
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuOpen && navRef.current && !navRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [menuOpen]);

    // 4. Cursor Glow (განათების ეფექტი)
    useEffect(() => {
        const nav = navRef.current;
        if (!nav) return;
        const move = (e) => {
            const rect = nav.getBoundingClientRect();
            nav.style.setProperty("--x", `${e.clientX - rect.left}px`);
            nav.style.setProperty("--y", `${e.clientY - rect.top}px`);
        };
        nav.addEventListener("mousemove", move);
        return () => nav.removeEventListener("mousemove", move);
    }, []);

    const handleScrollLink = (path) => {
        setMenuOpen(false);
        if (path.startsWith("#")) {
            if (location.pathname !== "/") {
                navigate("/");
                setTimeout(() => {
                    document.querySelector(path)?.scrollIntoView({ behavior: "smooth" });
                }, 100);
            } else {
                document.querySelector(path)?.scrollIntoView({ behavior: "smooth" });
            }
        } else {
            navigate(path);
        }
    };

    const magnet = (e) => {
        const el = e.currentTarget;
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        el.style.transform = `translate(${x * 0.15}px, ${y * 0.15}px)`;
    };

    const reset = (e) => {
        e.currentTarget.style.transform = "translate(0,0)";
    };

    const links = [
        { label: "ძებნა", path: "/find-teacher" },
        { label: "მიმოხილვა", path: "#aboutUs" },
        { label: "ფასები", path: "#pricing" },
        { label: "რეკლამა", path: "/cooperate-with-us" },
    ];

    return (
        <>
            <motion.nav
                ref={navRef}
                className={`navbar ${isScrolled ? "scrolled" : ""}`}
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
                <div className="navbar-inner">
                    {/* LOGO */}
                    <div className="navbar-logo" onClick={() => navigate("/")}>
                        <img src={logo} alt="Teacher Hub" />
                        <span>Teacher Hub</span>
                    </div>

                    {/* LINKS & AUTH */}
                    <div className={`navbar-links ${menuOpen ? "open" : ""}`}>
                        <div className="nav-navigation-group">
                            {links.map((item, i) => (
                                <motion.div
                                    key={i}
                                    className={`nav-item ${location.pathname === item.path ? "active" : ""}`}
                                    onClick={() => handleScrollLink(item.path)}
                                    onMouseMove={magnet}
                                    onMouseLeave={reset}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    {item.label}
                                </motion.div>
                            ))}
                        </div>

                        <div className="nav-auth-group">
                            <motion.button
                                className="login-btn"
                                onClick={() => navigate("/login")}
                                whileTap={{ scale: 0.95 }}
                            >
                                შესვლა
                            </motion.button>
                            <motion.button
                                className="register"
                                onClick={() => navigate("/register")}
                                whileHover={{ y: -2 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                რეგისტრაცია
                            </motion.button>
                        </div>
                    </div>

                    {/* HAMBURGER */}
                    <div
                        className={`navbar-hamburger ${menuOpen ? "open" : ""}`}
                        onClick={() => setMenuOpen(!menuOpen)}
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
            </motion.nav>

            {/* OVERLAY */}
            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        className="navbar-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setMenuOpen(false)}
                    />
                )}
            </AnimatePresence>
        </>
    );
}